import { useEffect } from 'react';
import {
  useQuery, useQueryClient, useInfiniteQuery, UseQueryResult, UseInfiniteQueryResult, InfiniteData,
} from '@tanstack/react-query';
import { getGeofenceEvents } from '../../services/GeofencesService';
import { GeofenceEventsResponse } from '../../types/Geofences';
import { FeaturesManagement } from '../../../featuresControl';
import { mergeNewestIntoFirstPage } from '../../../utils/queryFilters';

function useGetGeofenceEvents(fleetId: string, query: string): UseQueryResult<GeofenceEventsResponse> {
  return useQuery(
    ['geofences', fleetId, query],
    async (): Promise<GeofenceEventsResponse> => getGeofenceEvents(fleetId, query),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
    },
  );
}

export function useGetGeofenceEventsLazy(fleetId: string, payload?: any, query?: string, deps?: any): UseInfiniteQueryResult<GeofenceEventsResponse> {
  const queryClient = useQueryClient();
  const queryKey = ['geofences-lazy', fleetId, query];

  const infiniteQuery = useInfiniteQuery(
    queryKey,
    async ({ pageParam = query }): Promise<GeofenceEventsResponse> => getGeofenceEvents(fleetId, pageParam),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => (lastPage?.total ? ((lastPage.limit + lastPage.skip + 1) < lastPage.total) : undefined),
    },
  );

  const { data: latestPage } = useQuery(
    ['geofences-lazy-latest', fleetId, query, deps],
    async (): Promise<GeofenceEventsResponse> => getGeofenceEvents(fleetId, query as string),
    {
      retry: 1,
      retryDelay: 3000,
      refetchOnWindowFocus: false,
      refetchInterval: FeaturesManagement.productivity.refetchIntervalLazyLists,
      enabled: !!infiniteQuery.data,
    },
  );

  useEffect(() => {
    if (!latestPage) return;
    queryClient.setQueryData<InfiniteData<GeofenceEventsResponse>>(
      queryKey,
      (existing) => mergeNewestIntoFirstPage(existing, latestPage),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPage]);

  return infiniteQuery;
}

export default useGetGeofenceEvents;
